import { useEffect, useState } from 'react';
import Papa from 'papaparse';
import useLeadImport from '../../../../data/useLeadImport';
import LoadingWheel from '../../../shared/LoadingWheel';
import { CheckFeatureFlag } from '../../../../hooks/useFeatureFlag';

function CSVToJSONConverter({ }) {
  const { importLeads } = useLeadImport("CSVToJSONImporter");
  const isTME = CheckFeatureFlag("TME");
  const [isLoading, setIsLoading] = useState(false);
  const [importSource, setImportSource] = useState("fads");
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(false);

  const handleFileChange = (event) => {
    setIsLoading(true);
    setError(false);
    setSuccess(false);
    const file = event.target.files[0];

    Papa.parse(file, {
      header: false,
      complete: (results) => {
        var headers = getHeaders(results);
        var data = getData(results);

        var csvContent = Papa.unparse({
          fields: headers,
          data: data
        });

        const parser = parseType();
        parser(csvContent);
      }
    });
  };

  // Return correct header row per source
  const getHeaders = (results) => ({
    fads: getFadsHeaders(results),
    motorwise: results.data[0],
    removemycar: results.data[0],
  })[importSource];

  const getFadsHeaders = (results) => {
    const headerRow = results.data.find(
      row => row && row[0] && row[0].toString().startsWith("CollectID")
    );

    return headerRow ?? results.data[2]; // fallback if nothing matches
  }

  // Return correct data slice per source
  const getData = (results) => ({
    fads: results.data.slice(3),
    motorwise: results.data.slice(1),
    removemycar: results.data.slice(1),
  })[importSource];

  // Pick parser function
  const parseType = () => ({
    fads: fadsParser,
    motorwise: motorwiseParser,
    removemycar: removeMyCarParser,
  })[importSource];

  const saveNewLeads = (leads) => {
    if (!leads || leads.length === 0) {
      setIsLoading(false);
      setError(true);
      return;
    }

    var body = { leads };

    // console.log("Importing leads:", body);
    importLeads.mutate(body, {
      onSuccess: () => setSuccess(true),
      onError: (err) => {
        console.log(err);
        setError(true);
      },
      onSettled: () => setIsLoading(false),
    });
  };

  const fadsParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          var mapped = {
            CCID: lead["CCID"],
            Source: lead["Source"],
            Website: 2,
            Price: lead["Sales Price"],
            Commission: lead["Referral Fee"],
            PaymentType: lead["Payment Type"],
            AccountName: lead["Account Name"],
            AccountNumber: lead["Account Number"],
            SortCode: lead["Sort Code"],
            PaymentReference: lead["Payment Reference"],
            CardNumber: lead["Card Number"],
            PayeeDOB: lead["Payee DOB"],
            ChequeName: lead["Cheque Name"],
            ChequeNumber: lead["Cheque Number"],
            PremiumPriceUsed: lead["Premium Price Used"],
            Scheme: lead["Scheme"],
            VehicleGrade: lead["Vehicle Grade"],
            CentralPayment: lead["Central Payment"],
            Contact: {
              Fullname: `${lead["Customer Title"]} ${lead["Customer First Name"]} ${lead["Customer Last Name"]}`.trim(),
              Telephone1: lead["Customer Telephone (main)"],
              Telephone2: lead["Customer Telephone (alternative)"],
              Email: lead["Customer Email"],
              Organisation: lead["Customer Organisation"],
              Line1: `${lead["Customer Building"]} ${lead["Customer Street"]}`.trim(),
              Line2: lead["Customer Locality"],
              Town: lead["Customer Town"],
              County: lead["Customer County"],
              Postcode: lead["Customer Postcode"],
            },
            Vehicle: {
              Vrm: lead["VRM"],
              Make: lead["Make"],
              Model: lead["Model"],
              Telephone1: lead["Location Telephone (main)"],
              Telephone2: lead["Location Telephone (alternative)"],
              Line1: `${lead["Location Building"]} ${lead["Location Street"]}`.trim(),
              Line2: lead["Location Locality"],
              Town: lead["Location Town"],
              County: lead["Location County"],
              Postcode: lead["Location Postcode"],
              VehiclePremium: lead["Premium Price Used"] === "TRUE",
              IsDropOff: isTME,
            }
          };

          if (mapped.Vehicle.Vrm) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const motorwiseParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {
          var mapped = {
            CCID: lead["id"],
            Source: lead["siteName"],
            Website: 4,
            Price: parseInt(lead["quotedValue"]) || 0,
            Commission: 30,
            Contact: {
              Fullname: `${lead["title"]} ${lead["firstName"]} ${lead["lastName"]}`.trim(),
              Telephone1: lead["phone"],
              Telephone2: lead["alternatePhone"],
              Email: lead["email"],
              Line1: lead["addressLine1"]?.trim(),
              Line2: lead["addressLine2"],
              Town: lead["addressLine3"],
              County: "",
              Postcode: lead["postcode"],
            },
            Vehicle: {
              Vrm: lead["vrm"],
              Make: lead["make"],
              Model: lead["model"],
              Telephone1: lead["phone"],
              Telephone2: lead["alternatePhone"],
              Line1: lead["addressLine1"]?.trim(),
              Line2: lead["addressLine2"],
              Town: lead["addressLine3"],
              County: "",
              Postcode: lead["postcode"],
              VehiclePremium: lead["premiumCalibration"] === "1",
              VehicleNotes: lead["userComments"],
              IsDropOff: isTME,
            }
          };

          if (mapped.Vehicle.Vrm) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };

  const removeMyCarParser = (csv) => {
    Papa.parse(csv, {
      header: true,
      complete: (results) => {
        let leads = [];

        results.data.forEach(lead => {

          const status = lead["ApprovalStatusDescription"]?.trim().toLowerCase();
          if (status !== "arrange collection") {
            return; // skip this lead
          }

          // Strip HTML tags from notes
          const cleanNote = lead["Quote Notes"]
            ? lead["Quote Notes"].replace(/<[^>]*>/g, "").trim()
            : "";

          // Ignore "No Contact" notes
          const shouldIncludeNote =
            cleanNote &&
            cleanNote !== "A message was sent to the seller for No Contact";

          const mapped = {
            CCID: lead["QuoteId"],
            Source: "RemoveMyCar",
            Website: 8,
            Price: parseInt(lead["Price"]) || 0,
            Commission: 0,
            OfficeNotes: shouldIncludeNote ? cleanNote : "",
            Contact: {
              Fullname: lead["Customer Name"]?.trim(),
              Telephone1: lead["Phone"],
              Telephone2: "",
              Email: lead["Email"],
              Line1: "",
              Line2: lead["Locality"] || "",
              Town: lead["Town"],
              County: lead["County"],
              Postcode: lead["Postcode"],
            },
            Vehicle: {
              Vrm: lead["RegNo"],
              Make: lead["Combined_Make"],
              Model: lead["Combined_Model"],
              Telephone1: lead["Phone"],
              Telephone2: "",
              Line1: "",
              Line2: lead["Locality"] || "",
              Town: lead["Town"],
              County: lead["County"],
              Postcode: lead["Postcode"],
              VehiclePremium: false,
              IsDropOff: isTME,
            },
          };

          if (mapped.Vehicle.Vrm) leads.push(mapped);
        });

        saveNewLeads(leads);
      }
    });
  };


  if (isLoading) {
    return (
      <div className="flex justify-center">
        <LoadingWheel />
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="w-1/4 mb-12">
        <label className="w-full text-left">
          Import Source
        </label>
        <select className="w-full" value={importSource} onChange={(e) => setImportSource(e.target.value)}>
          <option value="fads">Fads</option>
          <option value="motorwise">Motorwise</option>
          <option value="removemycar">Remove My Car</option>
        </select>
      </div>

      <div className="mx-auto">
        <input
          id="import"
          type="file"
          className="inline-flex items-center rounded-md border border-transparent bg-primary-600 px-8 py-4 text-xl font-medium leading-4 text-white shadow-sm hover:bg-primary-700"
          onChange={handleFileChange}
        />

        <div className="mt-4">
          {success && <p className="text-lg text-green-500">Import Successful!</p>}
          {error && <p className="text-lg text-red-500">Error - Import Unsuccessful</p>}
        </div>
      </div>
    </div>
  );
}

export default CSVToJSONConverter;
